import documentsData from '@/mockData/documents.json'
import documentTypesData from '@/mockData/documentTypes.json'
import DocumentTypesService from '@/services/api/documentTypes.services'
import ApiClient from '@/libs/http-client'

const USER_DOCUMENTS_ENDPOINT = '/api/user-documents'

const normalizeDocument = (doc) => ({
    ...doc,
    documentTypeId: doc.documentTypeId ?? doc.document_type_id ?? doc.type_id,
    employeeId: doc.employeeId ?? doc.employee_id ?? doc.user_id,
    fileName: doc.fileName ?? doc.file_name ?? doc.original_name,
    fileSize: doc.fileSize ?? doc.file_size,
    fileType: doc.fileType ?? doc.file_type ?? doc.mime_type,
    uploadedAt: doc.uploadedAt ?? doc.uploaded_at ?? doc.created_at,
    fileUrl: doc.fileUrl ?? doc.file_url,
})

const normalizeEmployeeDocument = (doc) => {
    const normalized = normalizeDocument(doc)
    if (!normalized.fileUrl && (normalized.file instanceof Blob || normalized.file instanceof File)) {
        normalized.fileUrl = URL.createObjectURL(normalized.file)
    }
    return normalized
}

const state = {
    documents: documentsData,
    documentTypes: documentTypesData,
    isLoading: false,
    employeeDocuments: {},
    clientDocuments: {},
}

const mutations = {
    ADD_DOCUMENT(state, form) {
        state.documents.push(form)
    },
    SET_DOCUMENT_TYPES(state, payload) {
        state.documentTypes = payload
    },
    ADD_DOCUMENT_TYPE(state, form) {
        state.documentTypes.push(form)
    },
    UPDATE_DOCUMENT_TYPE(state, { id, ...payload }) {
        const index = state.documentTypes.findIndex((dt) => dt.id === id)
        if (index !== -1) {
            state.documentTypes[index] = {
                ...state.documentTypes[index],
                ...payload,
            }
        }
    },
    DELETE_DOCUMENT_TYPE(state, id) {
        state.documentTypes = state.documentTypes.filter((dt) => dt.id !== id)
    },
    SET_LOADING(state, payload) {
        state.isLoading = payload
    },
    SET_EMPLOYEE_DOCUMENTS(state, { employeeId, documents }) {
        state.employeeDocuments = {
            ...state.employeeDocuments,
            [employeeId]: documents,
        }
    },
    ADD_EMPLOYEE_DOCUMENT(state, { employeeId, document }) {
        if (!state.employeeDocuments[employeeId]) {
            state.employeeDocuments[employeeId] = []
        }
        state.employeeDocuments[employeeId].push(document)
    },
    DELETE_EMPLOYEE_DOCUMENT(state, { employeeId, documentId }) {
        if (state.employeeDocuments[employeeId]) {
            state.employeeDocuments[employeeId] = state.employeeDocuments[
                employeeId
            ].filter((doc) => doc.id !== documentId)
        }
    },
    SET_CLIENT_DOCUMENTS(state, { clientId, documents }) {
        state.clientDocuments = {
            ...state.clientDocuments,
            [clientId]: documents,
        }
    },
    ADD_CLIENT_DOCUMENT(state, { clientId, document }) {
        if (!state.clientDocuments[clientId]) {
            state.clientDocuments[clientId] = []
        }
        state.clientDocuments[clientId].push(document)
    },
    DELETE_CLIENT_DOCUMENT(state, { clientId, documentId }) {
        if (state.clientDocuments[clientId]) {
            state.clientDocuments[clientId] = state.clientDocuments[
                clientId
            ].filter((doc) => doc.id !== documentId)
        }
    },
}

const actions = {
    addDocument({ commit }, form) {
        commit('ADD_DOCUMENT', form)
    },

    async fetchDocumentTypes({ commit }) {
        commit('SET_LOADING', true)
        try {
            const response = await DocumentTypesService.get('documentTypes')
            commit('SET_DOCUMENT_TYPES', response.data.data || [])
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async createDocumentType({ commit }, documentType) {
        commit('SET_LOADING', true)
        try {
            const payload = {
                name: documentType?.name,
            }
            const response = await DocumentTypesService.post('documentTypes', {}, payload)
            commit('ADD_DOCUMENT_TYPE', response.data.data)
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async updateDocumentType({ commit }, { id, ...payload }) {
        commit('SET_LOADING', true)
        try {
            const data = {
                name: payload?.name,
            }
            const response = await DocumentTypesService.put('documentType', id, data)
            commit('UPDATE_DOCUMENT_TYPE', { id, ...response.data.data })
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async deleteDocumentType({ commit }, id) {
        commit('SET_LOADING', true)
        try {
            const response = await DocumentTypesService.delete('documentType', id)
            commit('DELETE_DOCUMENT_TYPE', id)
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async uploadDocument(
        { commit },
        { employeeId, documentTypeId, file },
    ) {
        commit('SET_LOADING', true)
        try {
            const formData = new FormData()
            formData.append('user_id', employeeId)
            formData.append('type_id', documentTypeId)
            formData.append('file', file)
            const response = await ApiClient.post(USER_DOCUMENTS_ENDPOINT, formData, {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'multipart/form-data',
                },
            })
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async getEmployeeDocuments({ commit }, employeeId) {
        commit('SET_LOADING', true)
        try {
            const response = await ApiClient.get(USER_DOCUMENTS_ENDPOINT, {
                params: { user_id: employeeId },
            })
            const documents = (response.data.data || []).map(normalizeEmployeeDocument)
            commit('SET_EMPLOYEE_DOCUMENTS', { employeeId, documents })
            return Promise.resolve({ data: { data: documents } })
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async deleteDocument({ commit }, { employeeId, documentId }) {
        commit('SET_LOADING', true)
        try {
            await ApiClient.delete(`${USER_DOCUMENTS_ENDPOINT}/${documentId}`)
            commit('DELETE_EMPLOYEE_DOCUMENT', { employeeId, documentId })
            return Promise.resolve({ data: { success: true } })
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },
    downloadEmployeeDocument(_, documentId) {
        return ApiClient.get(`${USER_DOCUMENTS_ENDPOINT}/${documentId}/download`, {
            responseType: 'blob',
        })
    },
    async uploadClientDocument(
        { commit },
        { clientId, file, description },
    ) {
        commit('SET_LOADING', true)
        try {
            const formData = new FormData()
            formData.append('client_id', clientId)
            formData.append('description', description || '')
            formData.append('file', file)

            const response = await ApiClient.post('/api/client-documents', formData, {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'multipart/form-data',
                },
            })
            const document = normalizeDocument(response.data.data || {})
            commit('ADD_CLIENT_DOCUMENT', { clientId, document })
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },
    async getClientDocuments({ commit }, clientId) {
        commit('SET_LOADING', true)
        try {
            const response = await ApiClient.get('/api/client-documents', {
                params: { client_id: clientId },
            })
            const documents = (response.data.data || []).map(normalizeDocument)
            commit('SET_CLIENT_DOCUMENTS', { clientId, documents })
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },
    async deleteClientDocument({ commit }, { clientId, documentId }) {
        commit('SET_LOADING', true)
        try {
            await ApiClient.delete(`/api/client-documents/${documentId}`, {
                params: { client_id: clientId },
            })
            commit('DELETE_CLIENT_DOCUMENT', { clientId, documentId })
            return Promise.resolve({ data: { success: true } })
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },
    getClientDocument(_, documentId) {
        return ApiClient.get(`/api/client-documents/${documentId}`)
    },
    updateClientDocument(_, { documentId, payload }) {
        return ApiClient.patch(`/api/client-documents/${documentId}`, payload)
    },
    downloadClientDocument(_, documentId) {
        return ApiClient.get(`/api/client-documents/${documentId}/download`, {
            responseType: 'blob',
        })
    },
    getClientDocumentsStorage() {
        return ApiClient.get('/api/client-documents-storage')
    },
}

const getters = {
    getData: (state) => state.documents,
    getTypes: (state) => state.documentTypes,
    getDocumentTypes: (state) => state.documentTypes,
    isLoading: (state) => state.isLoading,
    getEmployeeDocuments: (state) => (employeeId) => {
        return state.employeeDocuments[employeeId] || []
    },
    getClientDocuments: (state) => (clientId) => {
        return state.clientDocuments[clientId] || []
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
